// Synthesized audio engine. Web Audio synthesis; no shipped samples.
// Auto-resumes on first user gesture (browser autoplay policy).

/** Per-sound multipliers. Each is 1 = default; the synth engine
 *  multiplies these onto the patch's intrinsic values.
 *  - pitch:      ×freq of carrier (0.6..1.6).
 *  - brightness: ×filter cutoff (0.3..2.0).
 *  - decay:      ×envelope durations (0.4..2.0).
 *  - reverb:     ×reverb send (0..2.5; 0 = dry).
 *  - volume:     ×peak gain of envelope (0..2.0).
 *  - attack:     ×attack time of envelope (0.3..3.0; higher = softer onset).
 *
 *  Pan and crush were intentionally removed: stereo placement is now
 *  handled by a bus-level Haas widener (all sfx stay center-panned with
 *  a uniform widened image), and lo-fi grit is part of each individual
 *  patch's design instead of a free-floating user slider. */
export interface SfxParams {
  pitch: number;
  brightness: number;
  decay: number;
  reverb: number;
  volume: number;
  attack: number;
}

export const DEFAULT_SFX_PARAMS: SfxParams = {
  pitch: 1, brightness: 1, decay: 1, reverb: 1,
  volume: 1, attack: 1,
};

/** Named SFX preset — applies a sparse set of per-sound multiplier
 *  overrides on top of the defaults. User per-sfx tweaks layer on top
 *  of the active bank (user wins per key). */
export interface SfxBank {
  id: string;
  label: string;
  description: string;
  overrides: Partial<Record<SfxName, Partial<SfxParams>>>;
}

export interface AudioSettings {
  master: number;
  music: number;
  sfx: number;
  muted: boolean;
  /** Active SFX bank id — selects a curated set of per-sound multiplier
   *  overrides. User per-sfx tweaks (sfxParams below) layer on top
   *  of the bank's overrides. */
  sfxBank: string;
  /** Per-SFX customization map. Missing entries fall back to the
   *  active bank's overrides, then to DEFAULT_SFX_PARAMS. The user
   *  only persists keys they've actually tweaked. */
  sfxParams: Partial<Record<string, Partial<SfxParams>>>;
}

export const DEFAULT_AUDIO_SETTINGS: AudioSettings = {
  master: 0.7,
  music: 0.5,
  sfx: 0.9,
  muted: false,
  sfxBank: 'default',
  sfxParams: {},
};

export type SfxName =
  | 'move' | 'rotate' | 'softdrop' | 'harddrop' | 'lock'
  | 'hold' | 'clear1' | 'clear2' | 'clear3' | 'clear4'
  | 'tspin' | 'pc' | 'b2b' | 'combo' | 'levelup'
  | 'topout' | 'menu_move' | 'menu_select' | 'menu_back'
  | 'countdown' | 'go' | 'achievement' | 'error'
  | 'attack_send' | 'attack_receive' | 'purr';

/** Built-in SFX banks — curated multiplier overrides. The default
 *  bank ships empty (everything plays at its baked-in patch values).
 *  The rest selectively tweak tone/brightness/decay/reverb/volume/attack
 *  across the catalogue to give each bank a distinct overall character. */
export const SFX_BANKS: SfxBank[] = [
  {
    id: 'default',
    label: 'default',
    description: 'the original soft, subtle palette',
    overrides: {},
  },
  {
    id: 'muted',
    label: 'muted',
    description: 'softer onsets, dampened brightness, gentler hits',
    overrides: {
      move:      { brightness: 0.55, volume: 0.75, attack: 1.4 },
      rotate:    { brightness: 0.6,  volume: 0.8,  attack: 1.3 },
      softdrop:  { brightness: 0.55, volume: 0.7,  attack: 1.4 },
      harddrop:  { brightness: 0.6,  volume: 0.85, attack: 1.5, decay: 1.15 },
      lock:      { brightness: 0.6,  volume: 0.8,  attack: 1.4 },
      hold:      { brightness: 0.6,  volume: 0.8,  attack: 1.4 },
      clear1:    { brightness: 0.7,  attack: 1.3 },
      clear2:    { brightness: 0.7,  attack: 1.3 },
      clear3:    { brightness: 0.7,  attack: 1.3 },
      clear4:    { brightness: 0.75, attack: 1.2 },
      tspin:     { brightness: 0.7,  attack: 1.3 },
      menu_move: { brightness: 0.55, volume: 0.7,  attack: 1.5 },
      menu_select:{ brightness: 0.65, attack: 1.3 },
      menu_back: { brightness: 0.55, attack: 1.4 },
      countdown: { brightness: 0.65 },
    },
  },
  {
    id: 'bright',
    label: 'bright',
    description: 'crisp, sparkly, more high-end air',
    overrides: {
      move:      { brightness: 1.55, pitch: 1.06, attack: 0.7 },
      rotate:    { brightness: 1.5,  pitch: 1.05, attack: 0.75 },
      softdrop:  { brightness: 1.5,  pitch: 1.05, attack: 0.7 },
      harddrop:  { brightness: 1.4,  pitch: 1.02, attack: 0.7 },
      lock:      { brightness: 1.45, attack: 0.7 },
      hold:      { brightness: 1.45, pitch: 1.05 },
      clear1:    { brightness: 1.5,  pitch: 1.05 },
      clear2:    { brightness: 1.5,  pitch: 1.05 },
      clear3:    { brightness: 1.55, pitch: 1.06 },
      clear4:    { brightness: 1.6,  pitch: 1.06 },
      tspin:     { brightness: 1.55 },
      pc:        { brightness: 1.5,  pitch: 1.04 },
      menu_move: { brightness: 1.5,  pitch: 1.06, attack: 0.7 },
      menu_select:{ brightness: 1.5, pitch: 1.05 },
      countdown: { brightness: 1.5,  pitch: 1.05 },
      go:        { brightness: 1.5,  pitch: 1.04 },
    },
  },
  {
    id: 'retro',
    label: 'retro',
    description: 'arcade-era chiptune punch — higher, drier, snappier',
    overrides: {
      move:      { pitch: 1.25, brightness: 1.4, decay: 0.65, reverb: 0.2, attack: 0.5 },
      rotate:    { pitch: 1.2,  brightness: 1.35, decay: 0.7, reverb: 0.2, attack: 0.55 },
      softdrop:  { pitch: 1.2,  brightness: 1.3, decay: 0.7, reverb: 0.2 },
      harddrop:  { pitch: 0.95, brightness: 1.2, decay: 0.65, reverb: 0.25, volume: 1.1 },
      lock:      { pitch: 1.15, brightness: 1.3, decay: 0.6, reverb: 0.2 },
      hold:      { pitch: 1.2,  brightness: 1.35, decay: 0.7, reverb: 0.2 },
      clear1:    { pitch: 1.2,  brightness: 1.3, decay: 0.7, reverb: 0.3 },
      clear2:    { pitch: 1.2,  brightness: 1.3, decay: 0.7, reverb: 0.3 },
      clear3:    { pitch: 1.2,  brightness: 1.35, decay: 0.7, reverb: 0.3 },
      clear4:    { pitch: 1.2,  brightness: 1.4, decay: 0.7, reverb: 0.3, volume: 1.1 },
      tspin:     { pitch: 1.2,  brightness: 1.4, decay: 0.7, reverb: 0.3 },
      pc:        { pitch: 1.15, brightness: 1.35, decay: 0.7, reverb: 0.3 },
      b2b:       { pitch: 1.15, brightness: 1.3, decay: 0.7, reverb: 0.3 },
      combo:     { pitch: 1.15, brightness: 1.3, decay: 0.7, reverb: 0.3 },
      levelup:   { pitch: 1.1,  brightness: 1.3, reverb: 0.3 },
      achievement:{ pitch: 1.15, brightness: 1.35, reverb: 0.3 },
      menu_move: { pitch: 1.25, brightness: 1.4, decay: 0.6, reverb: 0.2 },
      menu_select:{ pitch: 1.2, brightness: 1.35, decay: 0.6, reverb: 0.2 },
      menu_back: { pitch: 1.15, brightness: 1.3, decay: 0.6, reverb: 0.2 },
      countdown: { pitch: 1.2,  brightness: 1.35, reverb: 0.2 },
      go:        { pitch: 1.15, brightness: 1.3, reverb: 0.25 },
    },
  },
  {
    id: 'bold',
    label: 'bold',
    description: 'thicker, louder, more weight on every hit',
    overrides: {
      move:      { volume: 1.25, decay: 1.05 },
      rotate:    { volume: 1.3, decay: 1.1 },
      softdrop:  { volume: 1.15 },
      harddrop:  { volume: 1.5, decay: 1.35, brightness: 0.9 },
      lock:      { volume: 1.4, decay: 1.25, brightness: 0.9, reverb: 1.2 },
      hold:      { volume: 1.3, decay: 1.1 },
      clear1:    { volume: 1.25, decay: 1.15 },
      clear2:    { volume: 1.3, decay: 1.15 },
      clear3:    { volume: 1.35, decay: 1.2 },
      clear4:    { volume: 1.4, decay: 1.25, reverb: 1.3 },
      tspin:     { volume: 1.4, decay: 1.2, reverb: 1.3 },
      pc:        { volume: 1.4, decay: 1.3, reverb: 1.4 },
      b2b:       { volume: 1.35 },
      combo:     { volume: 1.3 },
      levelup:   { volume: 1.3, reverb: 1.3 },
      topout:    { volume: 1.4, decay: 1.3, reverb: 1.3 },
      achievement:{ volume: 1.35, reverb: 1.3 },
      attack_send:    { volume: 1.4 },
      attack_receive: { volume: 1.4 },
    },
  },
  {
    id: 'smooth',
    label: 'smooth',
    description: 'mellow, rounded, more reverb tail',
    overrides: {
      move:      { brightness: 0.8, attack: 1.3, reverb: 1.6 },
      rotate:    { brightness: 0.85, attack: 1.3, reverb: 1.6 },
      softdrop:  { brightness: 0.85, attack: 1.3, reverb: 1.4 },
      harddrop:  { brightness: 0.85, attack: 1.3, reverb: 1.7, decay: 1.15 },
      lock:      { brightness: 0.85, attack: 1.3, reverb: 1.8, decay: 1.15 },
      hold:      { brightness: 0.85, attack: 1.4, reverb: 1.5 },
      clear1:    { brightness: 0.9,  attack: 1.3, reverb: 1.7 },
      clear2:    { brightness: 0.9,  attack: 1.3, reverb: 1.7 },
      clear3:    { brightness: 0.95, attack: 1.3, reverb: 1.7 },
      clear4:    { brightness: 0.95, attack: 1.3, reverb: 1.8, decay: 1.1 },
      tspin:     { brightness: 0.95, attack: 1.3, reverb: 1.7 },
      pc:        { brightness: 0.95, attack: 1.3, reverb: 2.0 },
      b2b:       { brightness: 0.95, reverb: 1.5 },
      combo:     { brightness: 0.95, reverb: 1.5 },
      levelup:   { brightness: 0.95, reverb: 1.8 },
      achievement:{ brightness: 0.95, reverb: 1.8 },
      menu_move: { brightness: 0.85, attack: 1.3, reverb: 1.4 },
      menu_select:{ brightness: 0.9, attack: 1.3, reverb: 1.4 },
      menu_back: { brightness: 0.85, attack: 1.3, reverb: 1.4 },
      countdown: { brightness: 0.9, reverb: 1.5 },
      go:        { brightness: 0.95, reverb: 1.7 },
    },
  },
];

interface NoteOpts {
  freq: number;
  durMs: number;
  type?: OscillatorType;
  attackMs?: number;
  releaseMs?: number;
  gain?: number;
  freqEnd?: number;
  filterFreq?: number;
}

interface ScheduledSource {
  source: AudioScheduledSourceNode;
  end: number; // ctx time at which it stops
}

export type MusicVariant = 'neon' | 'arcade' | 'ambient' | 'lofi' | 'silent';

export class AudioEngine {
  ctx: AudioContext | null = null;
  masterGain!: GainNode;
  musicGain!: GainNode;
  musicSubGain!: GainNode;     // killable per session — for instant silence on style change
  sfxGain!: GainNode;           // dry SFX bus
  sfxReverbSend!: GainNode;     // small reverb tail for "big" SFX (PC, Tetris, Achievement)
  /** Entry point for every SFX note. Kept as a passthrough gain so the
   *  primitives below have one node to connect to; the stereo widening
   *  happens in the chain between this node and sfxGain. */
  private sfxPanner!: GainNode;
  settings: AudioSettings = { ...DEFAULT_AUDIO_SETTINGS };
  /** Name of the SFX currently being synthesized. Set at the top of
   *  play() and cleared in finally; nested fmNote / noiseBurst calls
   *  read it to look up per-SFX overrides in settings.sfxParams. */
  private currentSfxName: SfxName | null = null;
  private musicLoopHandle: number | null = null;
  private musicPlaying = false;
  private musicVariant: MusicVariant = 'neon';
  private musicLevel = 1;
  private musicSession = 0;
  private musicSources: ScheduledSource[] = [];
  private sfxLastAt = new Map<SfxName, number>();
  private sfxMinIntervalMs: Partial<Record<SfxName, number>> = {
    // Bumped from 32 → 55ms. Combined with the trimmed move envelope
    // (durMs 20 / releaseMs 20 ≈ 40ms total), this guarantees the
    // previous move's voices have decayed before the next fires, so
    // back-to-back DAS taps don't beat against each other.
    move: 55,
    softdrop: 60,
    rotate: 28,
    // Menu navigation rate-limit: holding arrow-down across a long list
    // would otherwise machine-gun the SFX. 80ms feels alive without
    // becoming buzz.
    menu_move: 80,
  };
  /** Round-robin counters for SFX that fire in rapid succession. The
   *  patches below pick a variant indexed by the counter so identical
   *  voices can never stack on each other — every consecutive call has
   *  a subtly different timbre, breaking the "machine gun" interference
   *  pattern that single-voice patches produce under DAS / auto-shift. */
  private moveVariantIdx = 0;
  private rotateVariantIdx = 0;
  /** Move variants: small pitch / timbre offsets per slot. Four slots
   *  is enough to make any pair within the rate-limit window obviously
   *  different, while keeping the click family of sounds consistent. */
  private static readonly MOVE_VARIANTS = [
    { pitch: 540, modRatio: 4.0, modIndex: 2.2, gain: 0.085, filterHz: 3200 },
    { pitch: 560, modRatio: 4.0, modIndex: 2.0, gain: 0.080, filterHz: 3300 },
    { pitch: 525, modRatio: 3.6, modIndex: 2.4, gain: 0.090, filterHz: 3100 },
    { pitch: 548, modRatio: 4.4, modIndex: 2.1, gain: 0.082, filterHz: 3250 },
  ];
  private static readonly ROTATE_VARIANTS = [
    { pitch: 410, modRatio: 1.5, modIndex: 3.5 },
    { pitch: 425, modRatio: 1.6, modIndex: 3.3 },
    { pitch: 395, modRatio: 1.4, modIndex: 3.7 },
  ];

  init(): void {
    if (this.ctx) return;
    const Ctor = (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
    this.ctx = new Ctor();
    this.masterGain = this.ctx.createGain();
    this.musicGain = this.ctx.createGain();
    this.musicSubGain = this.ctx.createGain();
    this.sfxGain = this.ctx.createGain();
    this.sfxReverbSend = this.ctx.createGain();
    this.sfxPanner = this.ctx.createGain();
    // ─── Reverb bus (Schroeder-style: 3-tap feedback delay through a low-pass)
    // gives a short, dark room tail without convolving an impulse response.
    // Shorter, drier reverb than before. Feedback 0.42 -> 0.20 cuts the
    // tail to roughly 350 ms; wet 0.55 -> 0.32 keeps payoff chimes from
    // piling up into an endless ringing.
    const dl1 = this.ctx.createDelay(0.5); dl1.delayTime.value = 0.053;
    const dl2 = this.ctx.createDelay(0.5); dl2.delayTime.value = 0.089;
    const dl3 = this.ctx.createDelay(0.5); dl3.delayTime.value = 0.137;
    const fb  = this.ctx.createGain();     fb.gain.value      = 0.20;
    const damp = this.ctx.createBiquadFilter();
    damp.type = 'lowpass'; damp.frequency.value = 1800; damp.Q.value = 0.5;
    const wet = this.ctx.createGain();     wet.gain.value     = 0.32;
    this.sfxReverbSend.connect(dl1); dl1.connect(damp);
    this.sfxReverbSend.connect(dl2); dl2.connect(damp);
    this.sfxReverbSend.connect(dl3); dl3.connect(damp);
    damp.connect(fb); fb.connect(dl1); fb.connect(dl2);
    damp.connect(wet); wet.connect(this.sfxGain);

    // Dry SFX path: sfxPanner (entry gain) → saturation → stereo widener
    // → sfxGain → master. Saturation adds gentle harmonic warmth across
    // all SFX so they feel analog rather than DC-pure. Curve is shaped
    // tanh with a slight knee.
    const sat = this.ctx.createWaveShaper();
    sat.curve = makeSaturationCurve(2.4) as Float32Array<ArrayBuffer>;
    sat.oversample = '2x';
    this.sfxPanner.connect(sat);

    // Bus-level Haas-effect stereo widener. The mono SFX signal is
    // split into two channels; the right side gets a short (~12 ms)
    // delay so the brain perceives the signal as wider than mono
    // without any left/right bias. A small mix of the un-widened mono
    // is kept so percussive transients stay tight (a pure Haas widener
    // on a click sounds flammed).
    const splitter = this.ctx.createChannelSplitter(2);
    const merger = this.ctx.createChannelMerger(2);
    const dryMixL = this.ctx.createGain();
    const dryMixR = this.ctx.createGain();
    const widenL = this.ctx.createGain();
    const widenR = this.ctx.createGain();
    const haasDelay = this.ctx.createDelay(0.05);
    haasDelay.delayTime.value = 0.012;
    // Mix proportions: ~60% widened, ~40% dry per side. Audible image
    // without losing transient punch.
    dryMixL.gain.value = 0.4;
    dryMixR.gain.value = 0.4;
    widenL.gain.value = 0.6;
    widenR.gain.value = 0.6;
    sat.connect(splitter);
    splitter.connect(dryMixL, 0); dryMixL.connect(merger, 0, 0);
    splitter.connect(dryMixR, 0); dryMixR.connect(merger, 0, 1);
    splitter.connect(widenL, 0);  widenL.connect(merger, 0, 0);
    splitter.connect(haasDelay, 0); haasDelay.connect(widenR); widenR.connect(merger, 0, 1);
    merger.connect(this.sfxGain);
    this.sfxGain.connect(this.masterGain);

    // Music chain: sub → music → master. Sub is the per-session kill switch.
    this.musicSubGain.connect(this.musicGain);
    this.musicGain.connect(this.masterGain);
    this.masterGain.connect(this.ctx.destination);

    this.applySettings();
    this.attachResume();
    // Warm the purr loop so the first cat pet plays instantly instead
    // of waiting on a fetch + decode.
    void this.loadPurrBuffer();
  }

  setSettings(s: Partial<AudioSettings>): void {
    this.settings = { ...this.settings, ...s };
    if (this.ctx) this.applySettings();
  }

  private applySettings(): void {
    if (!this.ctx) return;
    const m = this.settings.muted ? 0 : this.settings.master;
    this.masterGain.gain.cancelScheduledValues(this.ctx.currentTime);
    this.masterGain.gain.linearRampToValueAtTime(m, this.ctx.currentTime + 0.05);
    this.musicGain.gain.value = this.settings.music;
    // SFX boost: per-sound gains are calibrated quiet (so they don't pile up
    // when stacked) but the bus pushes them back up to a usable level.
    this.sfxGain.gain.value = this.settings.sfx * 2.0;
  }

  private attachResume(): void {
    const resume = () => {
      if (this.ctx && this.ctx.state === 'suspended') this.ctx.resume();
    };
    window.addEventListener('pointerdown', resume, { passive: true });
    window.addEventListener('keydown', resume, { passive: true });
  }

  // ─── Sound design primitives ────────────────────────────────────
  //
  // The current SFX palette aims for "soft, subtle, engaging" — sine and
  // triangle dominate, square / saw are used sparingly for rare emphasis
  // moments only (T-spin, top out). Each primitive applies its own gentle
  // low-pass so nothing in the high-frequency shelf can bite the ear.
  //
  // `pan` is a small per-call jitter so repeated SFX don't stack mono-center.
  // `reverbSend` mixes a tiny tail into the reverb bus for the "big" hits.

  /** Soft sine pulse. Attack/release explicitly controlled so we can have
   *  fast-attack ticks (1ms) or slower-bloom payoffs (8ms). All SFX
   *  funnel through sfxPanner; the bus-level Haas widener handles
   *  stereo placement, so primitives no longer take a pan argument. */
  private softNote(opts: {
    freq: number;
    durMs: number;
    type?: OscillatorType;
    gain?: number;
    freqEnd?: number;
    attackMs?: number;
    releaseMs?: number;
    filterHz?: number;       // low-pass cutoff
    filterQ?: number;
    detuneCents?: number;
    reverbSend?: number;     // 0..1 ratio of dry to wet
    startDelayMs?: number;
  }): void {
    if (!this.ctx) return;
    const start = this.ctx.currentTime + (opts.startDelayMs ?? 0) / 1000;
    const dur = opts.durMs / 1000;

    const osc = this.ctx.createOscillator();
    osc.type = opts.type ?? 'sine';
    osc.frequency.setValueAtTime(opts.freq, start);
    if (opts.detuneCents) osc.detune.setValueAtTime(opts.detuneCents, start);
    if (opts.freqEnd != null) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(1, opts.freqEnd), start + dur);
    }

    const env = this.ctx.createGain();
    const peak = opts.gain ?? 0.08;
    const a = Math.max(0.001, (opts.attackMs ?? 3) / 1000);
    const r = Math.max(0.005, (opts.releaseMs ?? 60) / 1000);
    env.gain.setValueAtTime(0, start);
    env.gain.linearRampToValueAtTime(peak, start + a);
    env.gain.exponentialRampToValueAtTime(0.0001, start + dur + r);

    const filt = this.ctx.createBiquadFilter();
    filt.type = 'lowpass';
    filt.frequency.value = opts.filterHz ?? 4000;
    filt.Q.value = opts.filterQ ?? 0.7;

    osc.connect(filt); filt.connect(env);
    env.connect(this.sfxPanner);
    if (opts.reverbSend && opts.reverbSend > 0) {
      const send = this.ctx.createGain();
      send.gain.value = opts.reverbSend;
      env.connect(send); send.connect(this.sfxReverbSend);
    }

    osc.start(start);
    osc.stop(start + dur + r + 0.05);
  }

  /** FM synthesis primitive — a carrier oscillator whose frequency is
   *  modulated by a second oscillator. This is what gives bells, wood
   *  blocks, electric pianos, metallic clicks etc. their character.
   *
   *  modRatio: ratio of modulator freq to carrier (2 = bell, 1 = square-ish,
   *    0.5 = woody, 3+ = metallic).
   *  modIndex: how loud the modulator is (0 = pure sine, 1 = mild colour,
   *    3+ = strongly inharmonic).
   *  Both modulator amp + carrier amp use independent envelopes so the
   *  timbre evolves over time (modulator decay → tone settles to sine). */
  private fmNote(opts: {
    freq: number;
    durMs: number;
    gain?: number;
    modRatio?: number;
    modIndex?: number;
    modDecayMs?: number;       // modulator envelope decay
    attackMs?: number;
    releaseMs?: number;
    freqEnd?: number;
    filterHz?: number;
    reverbSend?: number;
    startDelayMs?: number;
  }): void {
    if (!this.ctx) return;
    // Look up per-SFX parameter overrides (set on the active SFX name at
    // the top of play()). Each multiplier defaults to 1.0 so an untouched
    // patch sounds exactly like before; users select a bank or tune
    // individual sounds via the Audio settings tab.
    const params = this.getActiveSfxParams();
    const pitchMul = params.pitch;
    const brightMul = params.brightness;
    const decayMul = params.decay;
    const reverbMul = params.reverb;
    const volumeMul = params.volume;
    const attackMul = params.attack;
    const start = this.ctx.currentTime + (opts.startDelayMs ?? 0) / 1000;
    const dur = (opts.durMs / 1000) * decayMul;

    const modRatio = opts.modRatio ?? 2;
    const modIndex = opts.modIndex ?? 2.5;
    const modDecay = ((opts.modDecayMs ?? Math.min(180, opts.durMs * 0.6)) / 1000) * decayMul;
    const freq = opts.freq * pitchMul;
    const freqEnd = opts.freqEnd != null ? opts.freqEnd * pitchMul : null;

    // Carrier
    const carrier = this.ctx.createOscillator();
    carrier.type = 'sine';
    carrier.frequency.setValueAtTime(freq, start);
    if (freqEnd != null) {
      carrier.frequency.exponentialRampToValueAtTime(Math.max(1, freqEnd), start + dur);
    }

    // Modulator
    const modulator = this.ctx.createOscillator();
    modulator.type = 'sine';
    modulator.frequency.value = freq * modRatio;
    const modAmp = this.ctx.createGain();
    // Modulator amplitude = modIndex * carrier freq (canonical FM scaling).
    // Decays to ~10% so the tone settles toward pure sine at the tail.
    modAmp.gain.setValueAtTime(freq * modIndex, start);
    modAmp.gain.exponentialRampToValueAtTime(freq * modIndex * 0.1, start + modDecay);
    modulator.connect(modAmp);
    modAmp.connect(carrier.frequency);

    // Carrier envelope
    const env = this.ctx.createGain();
    const peak = (opts.gain ?? 0.08) * volumeMul;
    const a = Math.max(0.001, ((opts.attackMs ?? 2) / 1000) * attackMul);
    const r = Math.max(0.005, (opts.releaseMs ?? 80) / 1000) * decayMul;
    env.gain.setValueAtTime(0, start);
    env.gain.linearRampToValueAtTime(peak, start + a);
    env.gain.exponentialRampToValueAtTime(0.0001, start + dur + r);

    // Filter — brightness slider scales the cutoff so the user can dial
    // between dark/warm and bright/sharp without re-tuning each patch.
    const filt = this.ctx.createBiquadFilter();
    filt.type = 'lowpass';
    filt.frequency.value = (opts.filterHz ?? 4500) * brightMul;
    filt.Q.value = 0.7;

    carrier.connect(filt);
    filt.connect(env);
    env.connect(this.sfxPanner);
    if (opts.reverbSend && opts.reverbSend > 0) {
      const send = this.ctx.createGain();
      send.gain.value = opts.reverbSend * reverbMul;
      env.connect(send); send.connect(this.sfxReverbSend);
    }

    const end = start + dur + r + 0.05;
    carrier.start(start);
    carrier.stop(end);
    modulator.start(start);
    modulator.stop(end);
  }

  /** Stack of FM bells — used for line clears, chords, GO!, etc.
   *  Per-voice pan was removed; bus-level widener now handles the
   *  stereo image without biasing individual voices. */
  private fmChord(
    freqs: number[], durMs: number, gain: number,
    modRatio: number, modIndex: number,
    filterHz: number, reverbSend: number,
  ): void {
    const peak = gain / Math.sqrt(freqs.length);
    freqs.forEach((f) => {
      this.fmNote({
        freq: f, durMs, gain: peak,
        modRatio, modIndex,
        modDecayMs: Math.min(durMs * 0.35, 120),
        // Tight release — was durMs * 0.8 which sustained almost as long
        // as the note itself, causing a piling tinnitus-ish tail.
        attackMs: 3, releaseMs: Math.min(durMs * 0.25, 120),
        filterHz,
        reverbSend,
      });
    });
  }

  /** Sequenced FM bells — used for arpeggios (PC, level up, achievement). */
  private fmArpeggio(
    freqs: number[], totalMs: number, gain: number,
    modRatio: number, modIndex: number,
    filterHz: number, reverbSend: number,
  ): void {
    const step = totalMs / freqs.length;
    // Notes overlap modestly — was 1.8x (way too much, every note hung
    // through 2-3 successors) and now 1.0x with a fast release.
    const noteDur = step * 1.0;
    freqs.forEach((f, i) => {
      this.fmNote({
        freq: f, durMs: noteDur, gain,
        modRatio, modIndex,
        modDecayMs: Math.min(noteDur * 0.4, 90),
        attackMs: 2, releaseMs: Math.min(noteDur * 0.5, 90),
        filterHz,
        reverbSend,
        startDelayMs: i * step,
      });
    });
  }

  /** Filtered noise burst — used for percussive "knock" / "thump" textures. */
  private noiseBurst(opts: {
    durMs: number;
    gain?: number;
    type?: BiquadFilterType;
    filterHz?: number;
    filterQ?: number;
    attackMs?: number;
  }): void {
    if (!this.ctx) return;
    const params = this.getActiveSfxParams();
    const decayMul = params.decay;
    const brightMul = params.brightness;
    const volumeMul = params.volume;
    const attackMul = params.attack;
    const now = this.ctx.currentTime;
    const dur = (opts.durMs / 1000) * decayMul;
    const sr = this.ctx.sampleRate;
    const buf = this.ctx.createBuffer(1, Math.max(1, Math.ceil(sr * (dur + 0.04))), sr);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const filt = this.ctx.createBiquadFilter();
    filt.type = opts.type ?? 'bandpass';
    filt.frequency.value = (opts.filterHz ?? 1200) * brightMul;
    filt.Q.value = opts.filterQ ?? 1.2;
    const env = this.ctx.createGain();
    const a = Math.max(0.0005, ((opts.attackMs ?? 1) / 1000) * attackMul);
    env.gain.setValueAtTime(0, now);
    env.gain.linearRampToValueAtTime((opts.gain ?? 0.08) * volumeMul, now + a);
    env.gain.exponentialRampToValueAtTime(0.0005, now + dur);
    src.connect(filt); filt.connect(env);
    env.connect(this.sfxPanner);
    src.start(now);
    src.stop(now + dur + 0.05);
  }

  /** Detuned sine stack — soft "chime" texture for line clears. Per-voice
   *  pan was removed; bus-level widener now does the spatial work. */
  private softChord(opts: {
    freqs: number[];
    durMs: number;
    gain?: number;
    attackMs?: number;
    releaseMs?: number;
    detuneCents?: number;     // detune amount per voice (small = lush, big = chorus)
    filterHz?: number;
    reverbSend?: number;
  }): void {
    if (!this.ctx) return;
    const peak = (opts.gain ?? 0.10) / Math.sqrt(opts.freqs.length);
    const det = opts.detuneCents ?? 6;
    opts.freqs.forEach((f, i) => {
      const sign = i % 2 === 0 ? 1 : -1;
      const cents = sign * det * ((i % 3) + 1) / 3;
      this.softNote({
        freq: f,
        durMs: opts.durMs,
        type: 'sine',
        gain: peak,
        attackMs: opts.attackMs ?? 6,
        releaseMs: opts.releaseMs ?? 140,
        filterHz: opts.filterHz ?? 3800,
        detuneCents: cents,
        reverbSend: opts.reverbSend ?? 0.12,
      });
    });
  }

  /** Sequenced arpeggio of soft notes. */
  private softArpeggio(opts: {
    freqs: number[];
    totalMs: number;
    gain?: number;
    type?: OscillatorType;
    overlap?: number;        // 0..1 — fraction of step time the previous note lingers
    filterHz?: number;
    reverbSend?: number;
  }): void {
    if (!this.ctx) return;
    const step = opts.totalMs / opts.freqs.length;
    const noteDur = step * (1 + (opts.overlap ?? 0.6));
    opts.freqs.forEach((f, i) => {
      this.softNote({
        freq: f,
        durMs: noteDur,
        type: opts.type ?? 'sine',
        gain: opts.gain ?? 0.08,
        attackMs: 5,
        releaseMs: 220,
        filterHz: opts.filterHz ?? 3800,
        reverbSend: opts.reverbSend ?? 0.25,
        startDelayMs: i * step,
      });
    });
  }

  /** Sub-bass thump — sine bell for hard drop / topout. */
  private bodyHit(opts: {
    freq: number;
    freqEnd: number;
    durMs: number;
    gain: number;
  }): void {
    this.softNote({
      freq: opts.freq, freqEnd: opts.freqEnd,
      durMs: opts.durMs, type: 'sine',
      gain: opts.gain, attackMs: 1, releaseMs: 100,
      filterHz: 1400, filterQ: 0.5,
      reverbSend: 0.10,
    });
  }

  // ─── Continuous purr voice (cat companion) ─────────────────────────
  // A recorded purr (public/purr.ogg — silence-trimmed, loop-seam
  // crossfaded, normalized to the music spec) looping on an
  // AudioBufferSourceNode. Replaces the earlier synthesized purr.
  // Riding the SFX bus keeps it inside the sfx volume/mute path and
  // OUT of Howler's music chain — so it neither feeds the miniplayer
  // visualizer nor gets muffled by the pause duck. Honors the 'purr'
  // per-sfx tuning at start time: volume → level, pitch → playback
  // rate, brightness → lowpass cutoff, attack → fade-in. (decay /
  // reverb are inert for the recorded voice.)
  private purrBuffer: AudioBuffer | null = null;
  private purrFetch: Promise<AudioBuffer | null> | null = null;
  private purrNodes: { master: GainNode; src: AudioBufferSourceNode } | null = null;
  /** True between startPurr and stopPurr — guards the async buffer
   *  load so a quick hover-off before the fetch lands doesn't start
   *  an orphaned loop. */
  private purrWanted = false;

  /** Fetch + decode the purr loop once; cached for the session. */
  private loadPurrBuffer(): Promise<AudioBuffer | null> {
    if (this.purrBuffer) return Promise.resolve(this.purrBuffer);
    if (!this.purrFetch) {
      this.purrFetch = (async () => {
        try {
          const res = await fetch('/purr.ogg');
          if (!res.ok) return null;
          const raw = await res.arrayBuffer();
          if (!this.ctx) return null;
          this.purrBuffer = await this.ctx.decodeAudioData(raw);
          return this.purrBuffer;
        } catch {
          return null;
        }
      })();
    }
    return this.purrFetch;
  }

  startPurr(): void {
    if (!this.ctx) this.init();
    if (!this.ctx || this.purrNodes) return;
    this.purrWanted = true;
    void this.loadPurrBuffer().then((buf) => {
      if (!buf || !this.ctx || !this.purrWanted || this.purrNodes) return;
      this.currentSfxName = 'purr';
      const p = this.getActiveSfxParams();
      this.currentSfxName = null;
      const now = this.ctx.currentTime;
      const master = this.ctx.createGain();
      // Kill the construction default (gain 1.0) SYNCHRONOUSLY via the
      // value setter — relying on setValueAtTime(0, now) alone races
      // the first render quantum, and losing that race played a few
      // milliseconds of the file at full music loudness before the
      // swell engaged (the occasional blast on hover).
      master.gain.value = 0;
      master.gain.setValueAtTime(0, now);
      // Exponential swell-in — no onset bump. Base level sits ~7 dB
      // under the first cut (0.45): the file is normalized to music
      // loudness, which read clearly louder than the synthesized sfx
      // around it. The purr should be felt under the mix, not lead it.
      master.gain.setTargetAtTime(0.2 * p.volume, now + 0.01, 0.18 * p.attack);
      const filter = this.ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = Math.max(300, Math.min(20000, 16000 * p.brightness));
      filter.Q.value = 0.5;
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      src.playbackRate.value = p.pitch;
      src.connect(filter);
      filter.connect(master);
      // Random start offset so consecutive pets don't always open on
      // the same breath.
      src.start(now, Math.random() * buf.duration);
      // Join the bus LAST: by the time the graph can pull audio
      // through this node, every gain event above is already in its
      // timeline — the full-gain first quantum becomes impossible.
      master.connect(this.sfxPanner);
      this.purrNodes = { master, src };
    });
  }

  stopPurr(): void {
    this.purrWanted = false;
    if (!this.ctx || !this.purrNodes) return;
    const { master, src } = this.purrNodes;
    this.purrNodes = null;
    const now = this.ctx.currentTime;
    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(master.gain.value, now);
    master.gain.linearRampToValueAtTime(0, now + 0.3);
    window.setTimeout(() => {
      try { src.stop(); master.disconnect(); } catch { /* */ }
    }, 420);
  }

  /** Resolve the active SFX's parameter multipliers. Called by fmNote /
   *  noiseBurst at synth time; reads from settings.sfxParams. Missing
   *  entries fall back to DEFAULT_SFX_PARAMS. */
  private getActiveSfxParams(): SfxParams {
    const name = this.currentSfxName;
    // Resolve in three layers: defaults → active bank overrides → user
    // per-sfx overrides (user always wins). Missing keys at any layer
    // fall through to the next.
    const bank = SFX_BANKS.find((b) => b.id === this.settings.sfxBank) ?? SFX_BANKS[0]!;
    const bankOverride = name ? (bank.overrides[name] ?? {}) : {};
    const userOverride = name ? (this.settings.sfxParams?.[name] ?? {}) : {};
    return {
      pitch:      userOverride.pitch      ?? bankOverride.pitch      ?? DEFAULT_SFX_PARAMS.pitch,
      brightness: userOverride.brightness ?? bankOverride.brightness ?? DEFAULT_SFX_PARAMS.brightness,
      decay:      userOverride.decay      ?? bankOverride.decay      ?? DEFAULT_SFX_PARAMS.decay,
      reverb:     userOverride.reverb     ?? bankOverride.reverb     ?? DEFAULT_SFX_PARAMS.reverb,
      volume:     userOverride.volume     ?? bankOverride.volume     ?? DEFAULT_SFX_PARAMS.volume,
      attack:     userOverride.attack     ?? bankOverride.attack     ?? DEFAULT_SFX_PARAMS.attack,
    };
  }

  play(name: SfxName, intensity = 1): void {
    if (!this.ctx) this.init();
    if (!this.ctx) return;
    // Rate-limit spammy SFX (move during DAS hand-off, rotate spam, softdrop bursts).
    const now = performance.now();
    const minInt = this.sfxMinIntervalMs[name];
    if (minInt != null) {
      const last = this.sfxLastAt.get(name) ?? -Infinity;
      if (now - last < minInt) return;
      this.sfxLastAt.set(name, now);
    }
    // Record the active SFX name so nested fmNote / noiseBurst calls can
    // look up this sound's per-SFX parameter overrides. Clear it after
    // the switch falls through so unrelated voices (music, etc.) don't
    // inherit the wrong context.
    this.currentSfxName = name;
    try {
    switch (name) {
      // ─── Move — mechanical-keyboard tactile click ────────────────────
      // FM "click" with a brief noise transient and a sub. Round-robins
      // through 4 variants per call so back-to-back DAS taps can never
      // play the same exact timbre twice in a row — no interference
      // pattern from stacked identical voices, even at the fastest
      // legal repeat rate.
      case 'move': {
        const v = AudioEngine.MOVE_VARIANTS[this.moveVariantIdx]!;
        this.moveVariantIdx = (this.moveVariantIdx + 1) % AudioEngine.MOVE_VARIANTS.length;
        const pitch = v.pitch * (1 + (Math.random() - 0.5) * 0.04);
        this.noiseBurst({ durMs: 5, gain: 0.05, type: 'highpass',
          filterHz: v.filterHz, filterQ: 1.0, attackMs: 0.3 });
        this.fmNote({ freq: pitch, durMs: 20, gain: v.gain,
          modRatio: v.modRatio, modIndex: v.modIndex, modDecayMs: 10,
          attackMs: 0.5, releaseMs: 20, filterHz: v.filterHz });
        this.softNote({ freq: pitch / 4, durMs: 22, type: 'sine', gain: 0.05,
          attackMs: 1, releaseMs: 24, filterHz: 700 });
        break;
      }

      // ─── Rotate — rotational "thwk" with FM character. Round-robin
      //     across 3 variants for the same anti-beating reason as move.
      case 'rotate': {
        const v = AudioEngine.ROTATE_VARIANTS[this.rotateVariantIdx]!;
        this.rotateVariantIdx = (this.rotateVariantIdx + 1) % AudioEngine.ROTATE_VARIANTS.length;
        const pitch = v.pitch * (1 + (Math.random() - 0.5) * 0.04);
        this.noiseBurst({ durMs: 12, gain: 0.055, type: 'bandpass',
          filterHz: 1800, filterQ: 1.2, attackMs: 0.4 });
        this.fmNote({ freq: pitch, freqEnd: pitch * 0.86, durMs: 55, gain: 0.10,
          modRatio: v.modRatio, modIndex: v.modIndex, modDecayMs: 35,
          attackMs: 1, releaseMs: 70, filterHz: 2400, reverbSend: 0.06 });
        this.softNote({ freq: pitch / 2, durMs: 50, type: 'sine', gain: 0.055,
          attackMs: 1, releaseMs: 60, filterHz: 900 });
        break;
      }

      // ─── Soft drop — sub tick + brief noise ──────────────────────────
      case 'softdrop': {
        this.noiseBurst({ durMs: 5, gain: 0.035, type: 'bandpass',
          filterHz: 1400, filterQ: 1.4, attackMs: 0.3 });
        this.fmNote({ freq: 220, durMs: 22, gain: 0.06,
          modRatio: 2, modIndex: 1.6, modDecayMs: 10,
          attackMs: 0.5, releaseMs: 30, filterHz: 1500 });
        this.softNote({ freq: 110, durMs: 24, type: 'sine', gain: 0.05,
          attackMs: 1, releaseMs: 30, filterHz: 500 });
        break;
      }

      // ─── Hard drop — weighty impact ──────────────────────────────────
      case 'harddrop': {
        this.noiseBurst({ durMs: 60, gain: 0.11, type: 'bandpass',
          filterHz: 900, filterQ: 1.0, attackMs: 1 });
        this.fmNote({ freq: 140, freqEnd: 50, durMs: 140, gain: 0.20,
          modRatio: 0.5, modIndex: 4, modDecayMs: 110,
          attackMs: 1, releaseMs: 140, filterHz: 1200, reverbSend: 0.12 });
        this.softNote({ freq: 55, freqEnd: 36, durMs: 160, type: 'sine', gain: 0.16,
          attackMs: 1, releaseMs: 150, filterHz: 400 });
        break;
      }

      // ─── Lock — wood-block tok with resonance ────────────────────────
      case 'lock': {
        const pitch = 280 * (1 + (Math.random() - 0.5) * 0.04);
        this.noiseBurst({ durMs: 16, gain: 0.07, type: 'bandpass',
          filterHz: 2400, filterQ: 2.4, attackMs: 0.3 });
        this.fmNote({ freq: pitch, freqEnd: pitch * 0.78, durMs: 90, gain: 0.105,
          modRatio: 0.7, modIndex: 3.2, modDecayMs: 70,
          attackMs: 0.5, releaseMs: 110, filterHz: 1800, reverbSend: 0.14 });
        this.softNote({ freq: 95, freqEnd: 70, durMs: 80, type: 'sine', gain: 0.09,
          attackMs: 1, releaseMs: 90, filterHz: 500 });
        break;
      }

      // ─── Hold — refined "whoosh" with chorus ─────────────────────────
      case 'hold': {
        this.fmNote({ freq: 540, freqEnd: 760, durMs: 100, gain: 0.085,
          modRatio: 2.5, modIndex: 1.4, modDecayMs: 80,
          attackMs: 4, releaseMs: 100, filterHz: 3200, reverbSend: 0.15 });
        // Second voice was detuned slightly via a hard pan; with pan
        // gone, just shift the start a hair to keep the chorusing.
        this.fmNote({ freq: 540, freqEnd: 760, durMs: 100, gain: 0.04,
          modRatio: 2.5, modIndex: 1.4, modDecayMs: 80,
          attackMs: 5, releaseMs: 100, filterHz: 3200, reverbSend: 0.08,
          startDelayMs: 8 });
        break;
      }

      // ─── Line clears — crystalline bells, escalating chord ───────────
      case 'clear1': {
        this.fmChord([523, 784], 220, 0.11, 2, 1.6, 4200, 0.06);
        break;
      }
      case 'clear2': {
        this.fmChord([523, 659, 784], 260, 0.12, 2, 1.7, 4300, 0.08);
        break;
      }
      case 'clear3': {
        this.fmChord([523, 659, 784, 988], 300, 0.13, 2, 1.8, 4400, 0.10);
        break;
      }
      case 'clear4': {
        this.fmChord([330, 523, 659, 784, 988, 1319], 380, 0.16, 2, 2.0, 4600, 0.15);
        this.fmNote({ freq: 1568, durMs: 220, gain: 0.04,
          modRatio: 3, modIndex: 1.6, modDecayMs: 100,
          attackMs: 8, releaseMs: 200, filterHz: 5000, reverbSend: 0.20 });
        break;
      }

      // ─── T-spin — darker, dissonant cluster ──────────────────────────
      case 'tspin': {
        this.fmChord([196, 277, 392, 554], 320, 0.13, 1.4, 2.4, 2400, 0.14);
        break;
      }

      // ─── Perfect Clear — cascading bell arpeggio ─────────────────────
      case 'pc': {
        this.fmArpeggio([523, 659, 784, 988, 1175, 1568, 1976], 720, 0.10, 1.5, 2.0, 4200, 0.18);
        break;
      }

      // ─── B2B — short bright bell ─────────────────────────────────────
      case 'b2b': {
        this.fmNote({ freq: 988, freqEnd: 1318, durMs: 140, gain: 0.085,
          modRatio: 2, modIndex: 2.0, modDecayMs: 80,
          attackMs: 3, releaseMs: 120, filterHz: 4400, reverbSend: 0.20 });
        break;
      }

      // ─── Combo — pitched ping rising with combo length ──────────────
      case 'combo': {
        const f = 523 + intensity * 80;
        this.fmNote({ freq: f, durMs: 90, gain: 0.08,
          modRatio: 2.5, modIndex: 1.8, modDecayMs: 40,
          attackMs: 2, releaseMs: 80, filterHz: 4000, reverbSend: 0.14 });
        break;
      }

      // ─── Level up — bell arpeggio up ─────────────────────────────────
      case 'levelup': {
        this.fmArpeggio([523, 659, 784, 1047], 360, 0.10, 2, 1.8, 4200, 0.12);
        break;
      }

      // ─── Top out — heavy descend with reverb wash ────────────────────
      case 'topout': {
        this.fmNote({ freq: 320, freqEnd: 55, durMs: 900, gain: 0.20,
          modRatio: 0.5, modIndex: 3.5, modDecayMs: 700,
          attackMs: 6, releaseMs: 300, filterHz: 1200, reverbSend: 0.35 });
        this.noiseBurst({ durMs: 480, gain: 0.08, type: 'lowpass',
          filterHz: 700, filterQ: 0.7, attackMs: 16 });
        this.softNote({ freq: 110, freqEnd: 30, durMs: 900, type: 'sine', gain: 0.10,
          attackMs: 8, releaseMs: 300, filterHz: 400 });
        break;
      }

      // ─── UI — short FM clicks, less blippy than pure sines ──────────
      case 'menu_move': {
        const pitch = 660 * (1 + (Math.random() - 0.5) * 0.04);
        this.fmNote({ freq: pitch, durMs: 28, gain: 0.06,
          modRatio: 3, modIndex: 1.4, modDecayMs: 12,
          attackMs: 0.5, releaseMs: 40, filterHz: 3800 });
        break;
      }
      case 'menu_select': {
        this.noiseBurst({ durMs: 6, gain: 0.04, type: 'highpass',
          filterHz: 4000, filterQ: 1.0, attackMs: 0.3 });
        this.fmNote({ freq: 660, freqEnd: 990, durMs: 120, gain: 0.10,
          modRatio: 2, modIndex: 1.8, modDecayMs: 80,
          attackMs: 2, releaseMs: 110, filterHz: 4000, reverbSend: 0.15 });
        break;
      }
      case 'menu_back': {
        this.fmNote({ freq: 660, freqEnd: 330, durMs: 130, gain: 0.10,
          modRatio: 1.5, modIndex: 2.0, modDecayMs: 90,
          attackMs: 2, releaseMs: 110, filterHz: 2800, reverbSend: 0.12 });
        break;
      }

      // ─── Countdown / GO — clear and warm ──────────────────────────
      case 'countdown': {
        this.fmNote({ freq: 660, durMs: 160, gain: 0.13,
          modRatio: 2, modIndex: 1.4, modDecayMs: 90,
          attackMs: 4, releaseMs: 90, filterHz: 3200, reverbSend: 0.18 });
        break;
      }
      case 'go': {
        this.fmChord([523, 784, 1047], 320, 0.16, 2, 1.8, 4600, 0.12);
        break;
      }

      // ─── Achievement — short bright celebration arpeggio + sub ─────
      case 'achievement': {
        this.fmArpeggio([659, 880, 1175, 1568], 260, 0.12, 2, 2.0, 4200, 0.08);
        this.fmNote({ freq: 220, durMs: 100, gain: 0.10,
          modRatio: 0.5, modIndex: 2.5, modDecayMs: 60,
          attackMs: 1, releaseMs: 100, filterHz: 900, reverbSend: 0.12 });
        break;
      }

      // ─── Error — short dissonant double-buzz (hold rejected, invalid
      //     action). Deliberately reads as "denied" without being harsh.
      case 'error': {
        this.fmNote({ freq: 220, freqEnd: 196, durMs: 70, gain: 0.10,
          modRatio: 1.06, modIndex: 5, modDecayMs: 60,
          attackMs: 1, releaseMs: 60, filterHz: 1600 });
        this.fmNote({ freq: 185, freqEnd: 165, durMs: 90, gain: 0.09,
          modRatio: 1.06, modIndex: 5, modDecayMs: 70,
          attackMs: 1, releaseMs: 70, filterHz: 1400, startDelayMs: 85 });
        break;
      }

      // ─── Attack send — rising whoosh + bright punch when you launch
      //     garbage at the opponent. Intensity = line count (1..16+). ─────
      case 'attack_send': {
        const lines = Math.max(1, Math.min(16, intensity | 0));
        const baseFreq = 280 + lines * 60;
        // Tonal punch sweeping upward
        this.fmNote({ freq: baseFreq, freqEnd: baseFreq * 2.2, durMs: 220 + lines * 20, gain: 0.13,
          modRatio: 1.5, modIndex: 2.6, modDecayMs: 140,
          attackMs: 2, releaseMs: 180, filterHz: 4200, reverbSend: 0.20 });
        // Whoosh layer — bright bandpass tail
        this.noiseBurst({ durMs: 200 + lines * 18, gain: 0.09, type: 'bandpass',
          filterHz: 3600, filterQ: 1.2, attackMs: 4 });
        break;
      }

      // ─── Attack receive — heavier thump with low-pass tail, warns the
      //     player that garbage just arrived on their stack. ──────────────
      case 'attack_receive': {
        const lines = Math.max(1, Math.min(16, intensity | 0));
        this.fmNote({ freq: 140, freqEnd: 70, durMs: 280 + lines * 18, gain: 0.18,
          modRatio: 0.5, modIndex: 3.6, modDecayMs: 160,
          attackMs: 1, releaseMs: 230, filterHz: 900, reverbSend: 0.22 });
        this.noiseBurst({ durMs: 200 + lines * 12, gain: 0.10, type: 'lowpass',
          filterHz: 600, filterQ: 1.0, attackMs: 1 });
        break;
      }

      // ─── Purr — a short preview of the continuous purr voice (the
      //     cat itself uses startPurr/stopPurr). Skipped if the real
      //     voice is already running so the settings test button never
      //     doubles it up. ─────────────────────────────────────────────
      case 'purr': {
        if (this.purrNodes) break;
        this.startPurr();
        window.setTimeout(() => this.stopPurr(), 1600);
        break;
      }
    }
    } finally {
      // Clear the active-SFX context so unrelated synth voices (music,
      // future external callers) don't pick up this sound's overrides.
      this.currentSfxName = null;
    }
  }

  // ----- procedural music -----

  startMusic(variant: MusicVariant = 'neon'): void {
    if (!this.ctx) this.init();
    if (!this.ctx) return;
    if (variant === 'silent') { this.stopMusic(); return; }
    if (this.musicPlaying && this.musicVariant === variant) return;
    this.stopMusic();
    this.musicVariant = variant;
    this.musicPlaying = true;
    // Fade in over 200ms to mask any transient.
    this.musicSubGain.gain.cancelScheduledValues(this.ctx.currentTime);
    this.musicSubGain.gain.setValueAtTime(0, this.ctx.currentTime);
    this.musicSubGain.gain.linearRampToValueAtTime(1, this.ctx.currentTime + 0.2);
    this.scheduleMusic();
  }

  /** Stop music IMMEDIATELY (ramp gain to 0, cancel pending timers, stop sources). */
  stopMusic(): void {
    this.musicPlaying = false;
    this.musicSession += 1;
    if (this.musicLoopHandle != null) {
      window.clearTimeout(this.musicLoopHandle);
      this.musicLoopHandle = null;
    }
    if (!this.ctx) return;
    // Fast 60ms fade-out then hard cancel — avoids click while killing all sound quickly.
    const now = this.ctx.currentTime;
    try {
      this.musicSubGain.gain.cancelScheduledValues(now);
      this.musicSubGain.gain.setValueAtTime(this.musicSubGain.gain.value, now);
      this.musicSubGain.gain.linearRampToValueAtTime(0, now + 0.06);
    } catch { /* */ }
    // Hard-stop all known sources slightly after the fade.
    for (const s of this.musicSources) {
      try { s.source.stop(now + 0.08); } catch { /* */ }
    }
    this.musicSources.length = 0;
  }

  setMusicLevel(level: number): void { this.musicLevel = Math.max(1, level); }

  private scheduleMusic(): void {
    if (!this.musicPlaying || !this.ctx) return;
    const session = this.musicSession;
    const bus = this.musicSubGain;
    const variant = this.musicVariant;
    // Gentle intensity ramp with level (caps at L15).
    const levelBoost = 1 + 0.022 * Math.min(15, Math.max(0, this.musicLevel - 1));

    // Style-specific song parts.
    const style = STYLES[variant as Exclude<MusicVariant, 'silent'>];
    const tempo = style.tempo * levelBoost;
    const stepMs = (60_000 / tempo) / 2; // eighth notes
    const barSteps = 16;
    const startAt = this.ctx.currentTime + 0.05;

    // Pick a chord index per bar, cycled.
    const chordIdx = Math.floor(performance.now() / 6000) % style.progression.length;
    const chord = style.progression[chordIdx]!;
    const bassRoot = chord[0]!; // lowest note as bass root

    // Bass line — quarter notes following the chord root.
    for (let i = 0; i < barSteps; i += style.bassEvery) {
      const t = startAt + (i * stepMs) / 1000;
      this.scheduleNote({
        bus, freq: bassRoot / 2, when: t,
        durMs: stepMs * style.bassEvery - 30,
        type: 'sawtooth', gain: style.bassGain, filterFreq: 700, session,
      });
    }

    // Melody — pattern-driven phrase rather than random walk.
    const phrase = style.melodyPhrases[chordIdx % style.melodyPhrases.length]!;
    for (const [stepIdx, scaleDeg, durSteps, gainMul] of phrase) {
      const t = startAt + (stepIdx * stepMs) / 1000;
      const note = chord[scaleDeg % chord.length]! * (variant === 'ambient' ? 1 : 1);
      this.scheduleNote({
        bus, freq: note, when: t,
        durMs: stepMs * durSteps - 20,
        type: style.melodyType, gain: style.melodyGain * gainMul,
        filterFreq: style.melodyFilter, session,
      });
    }

    // Pad: long sustained chord under the melody (ambient only).
    if (variant === 'ambient' || variant === 'lofi') {
      for (const f of chord) {
        this.scheduleNote({
          bus, freq: f, when: startAt,
          durMs: stepMs * barSteps - 100,
          type: 'sine', gain: style.padGain ?? 0.04, filterFreq: 1500, session,
        });
      }
    }

    // Drums — kick on 1 and 3, snare on 2 and 4, hat on every 8th.
    if (style.drums) {
      const quarter = stepMs * 2;
      for (let q = 0; q < 4; q++) {
        const t = startAt + (q * quarter) / 1000;
        if (q === 0 || q === 2) this.scheduleKick(bus, t, session);
        if (q === 1 || q === 3) this.scheduleSnare(bus, t, session);
      }
      for (let i = 0; i < barSteps; i++) {
        if (i % 2 === 0 || variant === 'arcade') {
          const t = startAt + (i * stepMs) / 1000;
          this.scheduleHat(bus, t, style.hatGain, session);
        }
      }
    }

    // Schedule next bar ~50 ms before this one ends so it stitches seamlessly.
    const barMs = stepMs * barSteps;
    this.musicLoopHandle = window.setTimeout(() => {
      if (this.musicSession !== session) return; // canceled
      this.scheduleMusic();
    }, Math.max(50, barMs - 50));
  }

  private trackSource(s: AudioScheduledSourceNode, endTime: number, session: number): void {
    if (this.musicSession !== session) { try { s.stop(0); } catch { /* */ } return; }
    this.musicSources.push({ source: s, end: endTime });
    // Drop tracked sources whose end has passed.
    const now = this.ctx?.currentTime ?? 0;
    while (this.musicSources.length && this.musicSources[0]!.end < now) this.musicSources.shift();
  }

  private scheduleNote(opts: {
    bus: AudioNode; freq: number; when: number; durMs: number;
    type: OscillatorType; gain: number; filterFreq?: number; session: number;
  }): void {
    if (!this.ctx) return;
    if (this.musicSession !== opts.session) return;
    const osc = this.ctx.createOscillator();
    osc.type = opts.type;
    osc.frequency.value = opts.freq;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, opts.when);
    g.gain.linearRampToValueAtTime(opts.gain, opts.when + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, opts.when + opts.durMs / 1000);
    if (opts.filterFreq) {
      const f = this.ctx.createBiquadFilter();
      f.type = 'lowpass'; f.frequency.value = opts.filterFreq;
      osc.connect(f); f.connect(g);
    } else {
      osc.connect(g);
    }
    g.connect(opts.bus);
    const end = opts.when + opts.durMs / 1000 + 0.05;
    osc.start(opts.when);
    osc.stop(end);
    this.trackSource(osc, end, opts.session);
  }

  private scheduleKick(bus: AudioNode, when: number, session: number): void {
    if (!this.ctx || this.musicSession !== session) return;
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(110, when);
    osc.frequency.exponentialRampToValueAtTime(35, when + 0.15);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, when);
    g.gain.linearRampToValueAtTime(0.20, when + 0.005);
    g.gain.exponentialRampToValueAtTime(0.001, when + 0.18);
    osc.connect(g); g.connect(bus);
    osc.start(when);
    osc.stop(when + 0.22);
    this.trackSource(osc, when + 0.22, session);
  }

  private scheduleSnare(bus: AudioNode, when: number, session: number): void {
    if (!this.ctx || this.musicSession !== session) return;
    // Noise burst through bandpass + a tiny tonal hit
    const sr = this.ctx.sampleRate;
    const buf = this.ctx.createBuffer(1, Math.ceil(sr * 0.12), sr);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const f = this.ctx.createBiquadFilter();
    f.type = 'bandpass'; f.frequency.value = 1800; f.Q.value = 0.8;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.10, when);
    g.gain.exponentialRampToValueAtTime(0.001, when + 0.12);
    src.connect(f); f.connect(g); g.connect(bus);
    src.start(when);
    src.stop(when + 0.15);
    this.trackSource(src, when + 0.15, session);
  }

  private scheduleHat(bus: AudioNode, when: number, gain: number, session: number): void {
    if (!this.ctx || this.musicSession !== session) return;
    const sr = this.ctx.sampleRate;
    const buf = this.ctx.createBuffer(1, Math.ceil(sr * 0.04), sr);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const f = this.ctx.createBiquadFilter();
    f.type = 'highpass'; f.frequency.value = 7000;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(gain, when);
    g.gain.exponentialRampToValueAtTime(0.001, when + 0.04);
    src.connect(f); f.connect(g); g.connect(bus);
    src.start(when);
    src.stop(when + 0.06);
    this.trackSource(src, when + 0.06, session);
  }
}

// ─── WaveShaper curves ──────────────────────────────────────────────

/** Smooth tanh saturation curve. `drive` controls how hard the corners
 *  bend — 1 = nearly linear, 3 = audible harmonics, 5+ = obvious. */
function makeSaturationCurve(drive: number): Float32Array {
  const N = 4096;
  const c = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    const x = (i / (N - 1)) * 2 - 1;
    c[i] = Math.tanh(x * drive) / Math.tanh(drive);
  }
  return c;
}

// ----- Composed music styles -----
//
// Each style has:
//   - tempo (BPM)
//   - chord progression (each chord is an array of frequencies forming the chord)
//   - melody phrases (one per chord)
//       phrase: array of [stepIndex, scaleDegreeIndexIntoChord, durationSteps, gainMul]
//   - bass / drum config
// Chords are in Hz (matched to standard equal-temperament).

interface MusicStyle {
  tempo: number;
  progression: number[][]; // chords (each chord is a list of frequencies)
  melodyPhrases: [number, number, number, number][][];
  melodyType: OscillatorType;
  melodyGain: number;
  melodyFilter?: number;
  bassEvery: number; // bass note every N eighth notes
  bassGain: number;
  drums: boolean;
  hatGain: number;
  padGain?: number;
}

// Frequencies (octave 4 unless noted): C=261.63, D=293.66, E=329.63, F=349.23, G=392, A=440, B=493.88
// Higher octave doubles, lower octave halves.
const STYLES: Record<Exclude<MusicVariant, 'silent'>, MusicStyle> = {
  // Synth-wave style. Am - F - C - G progression.
  neon: {
    tempo: 104,
    progression: [
      [220.00, 261.63, 329.63, 440.00], // Am: A C E A
      [174.61, 220.00, 261.63, 349.23], // F:  F A C F
      [261.63, 329.63, 392.00, 523.25], // C:  C E G C
      [196.00, 246.94, 293.66, 392.00], // G:  G B D G
    ],
    melodyPhrases: [
      [[0,3,2,1.0],[2,2,2,0.9],[4,1,2,0.85],[8,3,2,1.0],[10,2,2,0.9],[12,1,4,0.8]],
      [[0,3,2,1.0],[3,2,1,0.8],[4,1,2,0.85],[7,2,1,0.8],[8,3,4,1.0],[12,1,2,0.7]],
      [[0,2,2,0.9],[2,3,2,1.0],[6,1,2,0.85],[8,2,2,0.9],[10,3,2,1.0],[14,0,2,0.7]],
      [[0,2,2,0.9],[4,3,2,1.0],[8,2,2,0.85],[10,1,2,0.8],[12,3,2,1.0]],
    ],
    melodyType: 'triangle',
    melodyGain: 0.06,
    melodyFilter: 2800,
    bassEvery: 2,
    bassGain: 0.07,
    drums: true,
    hatGain: 0.022,
  },

  // Arcade chiptune: C - G - Am - F. Fast, bright.
  arcade: {
    tempo: 132,
    progression: [
      [261.63, 329.63, 392.00, 523.25], // C
      [196.00, 246.94, 293.66, 392.00], // G
      [220.00, 261.63, 329.63, 440.00], // Am
      [174.61, 220.00, 261.63, 349.23], // F
    ],
    melodyPhrases: [
      [[0,2,1,1.0],[1,3,1,1.0],[2,2,1,0.85],[4,1,1,0.85],[5,2,1,0.85],[6,3,2,1.0],[10,2,2,0.9],[12,1,4,0.8]],
      [[0,1,1,1.0],[2,2,1,0.95],[4,3,1,0.95],[6,2,2,0.9],[10,1,2,0.85],[12,3,4,1.0]],
      [[0,3,1,1.0],[2,2,1,0.9],[4,1,1,0.85],[6,2,2,0.9],[10,3,2,1.0],[12,2,4,0.85]],
      [[0,2,1,0.95],[2,3,1,1.0],[4,2,1,0.9],[6,1,2,0.85],[10,2,2,0.9],[12,3,4,1.0]],
    ],
    melodyType: 'square',
    melodyGain: 0.05,
    melodyFilter: 3600,
    bassEvery: 1,
    bassGain: 0.05,
    drums: true,
    hatGain: 0.018,
  },

  // Ambient: slow, sustained, no drums.
  ambient: {
    tempo: 64,
    progression: [
      [196.00, 246.94, 293.66, 369.99], // Gm7-ish
      [174.61, 220.00, 261.63, 329.63], // Fmaj7-ish
      [220.00, 261.63, 329.63, 392.00], // Am7-ish
      [174.61, 220.00, 277.18, 349.23], // F#dim-ish
    ],
    melodyPhrases: [
      [[0,3,4,0.85],[6,2,4,0.8]],
      [[2,2,4,0.8],[8,3,4,0.85]],
      [[0,1,8,0.75]],
      [[4,2,4,0.75],[10,3,4,0.7]],
    ],
    melodyType: 'sine',
    melodyGain: 0.045,
    melodyFilter: 1600,
    bassEvery: 8,
    bassGain: 0.035,
    drums: false,
    hatGain: 0,
    padGain: 0.025,
  },

  // Lo-fi: slow, jazzy, soft drums.
  lofi: {
    tempo: 82,
    progression: [
      [220.00, 277.18, 329.63, 415.30], // Am(maj7)
      [196.00, 246.94, 293.66, 369.99], // Gm7
      [174.61, 220.00, 261.63, 329.63], // F maj
      [164.81, 207.65, 246.94, 311.13], // E♭ maj7
    ],
    melodyPhrases: [
      [[0,3,2,0.85],[3,2,1,0.7],[4,1,3,0.85],[10,2,2,0.8],[12,3,4,0.9]],
      [[0,2,2,0.8],[4,3,2,0.85],[7,1,1,0.7],[8,2,4,0.85]],
      [[0,1,2,0.75],[3,3,1,0.85],[4,2,4,0.85],[10,1,2,0.75]],
      [[0,3,4,0.85],[6,2,2,0.8],[10,1,4,0.8]],
    ],
    melodyType: 'sine',
    melodyGain: 0.045,
    melodyFilter: 2000,
    bassEvery: 4,
    bassGain: 0.05,
    drums: true,
    hatGain: 0.012,
    padGain: 0.018,
  },
};
